import { PaginationOptions, PaginatedResult } from 'types/api';
import { invalidate, invalidateStartsWith } from 'hooks/useSWR';
import { conversationMeKey } from './useConversationMe';
import { withPagination } from 'apis/utils';
import config from 'config';
import { Message } from './conversationTypes';
import { useEffect, useMemo } from 'react';
import { reloadConversationList } from './useConversationList';
import { reloadConversationRequestList } from './useConversationRequestList';
import usePaginatedResource from 'util/resource/usePaginatedResource';
import { fetched, IResource, isFetching } from 'util/resource';

export const conversationMessagesKey = (conversationId?: UUID, paginationOptions?: PaginationOptions) => {
  if (!conversationId || !paginationOptions) return undefined;
  return withPagination(`${config.COMPANIES_API_URL}/conversations/${conversationId}/messages`, paginationOptions);
};

export const reloadConversationMessages = (conversationId?: UUID) => {
  if (!conversationId) return;
  invalidateStartsWith([`${config.COMPANIES_API_URL}/conversations/${conversationId}/messages`]);
};

export const useConversationMessages = (
  conversationId: UUID | undefined,
  initialLimit: number,
  isConversationLoading = false,
  isNewConversation = false,
) => {
  const [state, reload, onSeeMore] = usePaginatedResource<Message>(
    (paginationOptions: PaginationOptions) => {
      if (!conversationId) return ''; // Return empty string, which will be converted to null in usePaginatedResource
      return conversationMessagesKey(conversationId, paginationOptions) || '';
    },
    { limit: initialLimit, page: 1 },
    undefined,
    {
      mode: 'append',
      uniqueSelector: (m: Message) => m.id,
      deps: [conversationId], // Reset when conversation changes - this will trigger new fetch when ID appears
    },
  );

  // Transform 404 errors or empty conversationId into empty state (conversation not started yet)
  const transformedState: IResource<PaginatedResult<Message>> = useMemo(() => {
    // Handle idle state (SWR not fetching because no key)
    if (state.state === 'idle') {
      // If conversation is still loading, show loading state for messages too
      if (isConversationLoading) {
        return isFetching();
      }
      // Otherwise return empty state (no conversation ID yet)
      return fetched({
        values: [],
        total: 0,
        page: 1,
        pages: 1,
        limit: initialLimit,
        pageSize: 0,
      });
    }

    // Legacy handling: If no conversationId and not idle (shouldn't happen but keep for safety)
    if (!conversationId && isConversationLoading) {
      return isFetching();
    }
    if (!conversationId) {
      return fetched({
        values: [],
        total: 0,
        page: 1,
        pages: 1,
        limit: initialLimit,
        pageSize: 0,
      });
    }
    // If we're still waiting for conversation to be determined, treat any errors as "loading"
    if (state.state === 'error' && isConversationLoading) {
      return isFetching();
    }
    // Transform 404 errors into empty state (conversation exists but no messages yet)
    if (state.state === 'error' && state.errorType === 'NotFound') {
      return fetched({
        values: [],
        total: 0,
        page: 1,
        pages: 1,
        limit: initialLimit,
        pageSize: 0,
      });
    }
    // For new conversations, treat any message errors as "no messages yet"
    // (conversation exists but hasn't been started)
    if (state.state === 'error' && isNewConversation) {
      return fetched({
        values: [],
        total: 0,
        page: 1,
        pages: 1,
        limit: initialLimit,
        pageSize: 0,
      });
    }
    return state;
  }, [state, initialLimit, conversationId, isConversationLoading, isNewConversation]);

  // Preserve side effects: invalidate related caches when page 1 loads
  useEffect(() => {
    if (transformedState.state === 'fetched' && conversationId && transformedState.resource.page === 1) {
      invalidate(conversationMeKey);
      reloadConversationList();
      reloadConversationRequestList();
    }
  }, [transformedState.state, conversationId, transformedState.state === 'fetched' && transformedState.resource.page]);

  return [transformedState, reload, onSeeMore] as const;
};
